import { useEffect, useMemo, useState } from 'react';
import { Box, Autocomplete, TextField, Switch, FormControlLabel } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuthentication } from 'src/features/authentication/context';
import { AssetType } from 'src/features/sites/types/types';
import { useCustomDashboardContext } from 'src/contexts/customDashboardContext/customDashboard.context';
import { AssetService } from 'src/services/assets/asset.service';
import { NewSite } from 'src/types/sites/sites';
import { ComponentType } from 'src/types/SCADA/componentsTypes';
// import { ComponentType } from 'src/types/SCADA/componentsTypes';

export const AssetDrawerContent = () => {
  const {
    customerId: { value: customerId },
    sites,
  } = useAuthentication();

  const { selectedNode, updateNodeData, nodes } = useCustomDashboardContext();

  // const isRotorDrawer = selectedNode?.data?.subType === ComponentType.Rotor;
  let assetLabel = '';

  if (
    selectedNode?.type === ComponentType.Tower ||
    selectedNode?.type === ComponentType.Well ||
    selectedNode?.type === ComponentType.WaterLevelShape
  ) {
    assetLabel = 'Select well asset';
  } else {
    assetLabel = 'Select pump asset';
  }

  const [formValues, setFormValues] = useState({
    isDisplayName: true,
    siteId: '',
    assetId: '',
    // activeValue: '',
  });

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  const isAssetsRequestEnabled = !!customerId && !!selectedSiteId;

  const { data: assets } = useQuery<AssetType[]>({
    queryKey: ['assets', customerId, selectedSiteId],
    queryFn: () => AssetService.getAll(customerId!, selectedSiteId!),
    staleTime: 1000 * 60 * 2,
    enabled: isAssetsRequestEnabled,
  });

  const siteOptions: NewSite[] = useMemo(() => sites?.value ?? [], [sites]);

  const onChangeHandler = (name: string, value: string | boolean | null) => {
    setFormValues((prevState) => {
      const updated = { ...prevState, [name]: value };

      if (selectedNode?.id) {
        updateNodeData(selectedNode.id, updated);
      }

      return updated;
    });
  };

  const selectedAssetObj = useMemo(() => {
    if (!assets) return null;
    return assets.find((a) => a.id === formValues.assetId) ?? null;
  }, [assets, formValues.assetId]);

  useEffect(() => {
    if (!selectedNode?.id) return;

    const findNode = nodes.find((node) => node.id === selectedNode?.id);

    const assetId = (findNode?.data as { assetId?: string })?.assetId ?? '';
    const siteId = (findNode?.data as { siteId?: string })?.siteId ?? '';
    const isDisplayName = (findNode?.data as { isDisplayName?: boolean })?.isDisplayName ?? true;
    // const activeValue = (findNode?.data as { activeValue?: string })?.activeValue ?? '';

    setFormValues({
      assetId,
      siteId,
      isDisplayName,
      // activeValue,
    });
    setSelectedSiteId(siteId);
  }, [selectedNode?.id, nodes]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Autocomplete
        value={siteOptions.find((s) => s.id === formValues.siteId) ?? null}
        size="small"
        onChange={(_, selectedSite) => {
          if (selectedSite) {
            setSelectedSiteId(selectedSite.id);
            onChangeHandler('siteId', selectedSite.id);

            onChangeHandler('assetId', '');
          } else {
            setSelectedSiteId('');
            onChangeHandler('siteId', null);
            onChangeHandler('assetId', '');
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Select Site" data-sm="select-site-input" />
        )}
        options={siteOptions}
        getOptionLabel={(option) => option.siteName}
        renderOption={(props, option) => (
          <li {...props} key={`${option.id}-${option.siteName}`}>
            {option.siteName}
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Autocomplete
        size="small"
        value={selectedAssetObj}
        onChange={(_, selectedAsset) => {
          if (selectedAsset) {
            onChangeHandler('assetId', selectedAsset.id);
          } else {
            onChangeHandler('assetId', null);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label={assetLabel} data-sm="select-pump-asset-input" />
        )}
        options={assets ?? []}
        getOptionLabel={(option) => option?.assetName}
        renderOption={(props, option) => (
          <li {...props} key={`${option.id}-${option.assetName}`}>
            {option.assetName}
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={!selectedSiteId}
      />
      {/* {isRotorDrawer && (
        <TextField
          size="small"
          value={formValues.activeValue}
          label="Active Value"
          name="activeValue"
          onChange={(e) => onChangeHandler('activeValue', e.target.value)}
        />
      )} */}
      <FormControlLabel
        control={
          <Switch
            checked={formValues.isDisplayName}
            name="isDisplayName"
            onChange={(e) => onChangeHandler(e.target.name, e.target.checked)}
          />
        }
        label="Display name"
      />
    </Box>
  );
};
